import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as CompanyService from '@/services/CompanyService';
import type { Company } from '@/types/company';

export const useCompanies = () =>
  useQuery({
    queryKey: ['companies'],
    queryFn: CompanyService.getCompanies,
  });

export const useCompany = (id: string) =>
  useQuery({
    queryKey: ['companies', id],
    queryFn: () => CompanyService.getCompany(id),
    enabled: !!id,
  });

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) =>
      CompanyService.createCompany(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Company, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => CompanyService.updateCompany(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['companies', id] });
    },
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: CompanyService.deleteCompany,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
};
